import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

import { ERPPage, ERPPageHeader, ERPKPIGrid, ERPKPICard } from '../../components/ui/ERPLayout';
import { salariosService } from '../../services/salariosService';
import { clientService } from '../../services/clientService';
import { employeeService } from '../../services/employeeService';
import { exportSalariosToPDF, exportSalariosToCSV } from './utils/exportUtils';
import { getQuickPeriodUTCRange, localDateToUTCStart, localDateToUTCEndExclusive } from '../../utils/dateRangeHelper';

// ─── Salary Calculation Rules ─────────────────────────────────────────────────
const calcularSalarioEmpleado = (tipo, peso) => {
  const t = (tipo || '')?.toUpperCase();
  const p = parseFloat(peso) || 0;
  if (t === 'EMPLATADO') return { importe: 25, tarifa: '25 € (Emplatado)', motivo: 'Tarifa fija emplatado' };
  if (t === 'PALETILLA') return { importe: 15, tarifa: '15 € (Paletilla)', motivo: 'Tarifa fija paletilla' };
  // NORMAL / JAMON
  if (p < 10) return { importe: 20, tarifa: '20 € (<10 kg)', motivo: 'Jamón menos de 10 kg' };
  return { importe: 25, tarifa: '25 € (≥10 kg)', motivo: 'Jamón 10 kg o más' };
};

// ─── Quick Date Filter Buttons ────────────────────────────────────────────────
const QUICK_FILTERS = [
  { key: 'today', label: 'Hoy' },
  { key: 'yesterday', label: 'Ayer' },
  { key: 'thisWeek', label: 'Esta Semana' },
  { key: 'lastWeek', label: 'Semana Pasada' },
  { key: 'thisMonth', label: 'Este Mes' },
  { key: 'lastMonth', label: 'Mes Pasado' },
  { key: 'thisQuarter', label: 'Este Trimestre' },
  { key: 'thisYear', label: 'Este Año' },
  { key: 'custom', label: 'Personalizado' },
];

// ─── Pagination ───────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const Pagination = ({ currentPage, totalPages, totalRecords, pageSize, onPageChange, onPageSizeChange }) => {
  const from = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalRecords);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
      style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
      <div className="flex items-center gap-4">
        <span className="text-sm font-oranienbaum text-white/50">
          Mostrando <span className="text-white/80 font-bold">{from}–{to}</span> de{' '}
          <span className="text-white/80 font-bold">{totalRecords}</span> registros
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs font-oranienbaum text-white/40">Por página:</span>
          <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e?.target?.value))}
            className="font-oranienbaum text-sm text-white/80 rounded-lg px-2 py-1 focus:outline-none"
            style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.12)' }}>
            {PAGE_SIZE_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(1)} disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">«</button>
        <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">‹ Anterior</button>
        <span className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum font-bold"
          style={{ background: 'rgba(214,239,78,0.12)', border: '1px solid rgba(214,239,78,0.25)', color: '#D6EF4E' }}>
          Página {currentPage} de {totalPages || 1}
        </span>
        <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">Siguiente ›</button>
        <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages}
          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-30 disabled:cursor-not-allowed text-white/60 hover:text-white hover:bg-white/8">»</button>
      </div>
    </div>
  );
};

// ─── Employee Summary Card ────────────────────────────────────────────────────
const EmployeeSummaryCard = ({ loncheados, empleados, selectedEmpleadoId, formatCurrency }) => {
  if (!selectedEmpleadoId || selectedEmpleadoId === 'all') return null;
  const emp = empleados?.find(e => String(e?.id) === String(selectedEmpleadoId));
  const empLoncheados = loncheados?.filter(l =>
    String(l?.empleadoId || l?.empleado_id) === String(selectedEmpleadoId)
  );

  const totalGenerado = empLoncheados?.reduce((s, l) => s + (parseFloat(l?.tarifaAplicada || l?.importe) || 0), 0);
  const totalACobrar = empLoncheados?.reduce((s, l) => {
    const calc = calcularSalarioEmpleado(l?.tipo, l?.pesoLoncheado || l?.peso_loncheado);
    return s + calc?.importe;
  }, 0);
  const jamones = empLoncheados?.filter(l => (l?.tipo || '')?.toUpperCase() === 'NORMAL')?.length;
  const paletillas = empLoncheados?.filter(l => (l?.tipo || '')?.toUpperCase() === 'PALETILLA')?.length;
  const emplatados = empLoncheados?.filter(l => (l?.tipo || '')?.toUpperCase() === 'EMPLATADO')?.length;
  const kgTotal = empLoncheados?.reduce((s, l) => s + (parseFloat(l?.pesoLoncheado || l?.peso_loncheado) || 0), 0);
  const lastWork = empLoncheados?.length > 0
    ? new Date(Math.max(...empLoncheados.map(l => new Date(l?.createdAt || l?.created_at))))
    : null;

  // Most worked client
  const clientCount = {};
  empLoncheados?.forEach(l => {
    const cn = l?.clienteNombre || l?.cliente_nombre || '—';
    clientCount[cn] = (clientCount?.[cn] || 0) + 1;
  });
  const topCliente = Object.entries(clientCount)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || '—';

  const rentabilidad = totalGenerado > 0 ? ((totalGenerado - totalACobrar) / totalGenerado * 100)?.toFixed(1) : 0;

  return (
    <div className="mb-5 bg-[#1A1A1A] rounded-xl border border-[#D6EF4E]/20 overflow-hidden"
      style={{ boxShadow: '0 0 20px rgba(214,239,78,0.06)' }}>
      <div className="px-5 py-3 border-b border-white/8 flex items-center gap-3"
        style={{ background: 'rgba(214,239,78,0.05)' }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(214,239,78,0.12)' }}>
          <Icon name="User" size={18} style={{ color: '#D6EF4E' }} />
        </div>
        <div>
          <h3 className="text-base font-bold font-oranienbaum text-[#D6EF4E]">
            {emp?.nombre || 'Empleado'}
          </h3>
          <p className="text-xs font-oranienbaum text-white/40">Resumen detallado del empleado</p>
        </div>
      </div>
      <div className="p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Generado', value: formatCurrency(totalGenerado), color: '#3A7F6B' },
          { label: 'Total a Cobrar', value: formatCurrency(totalACobrar), color: '#E05252' },
          { label: 'Nº Loncheados', value: empLoncheados?.length, color: '#D6EF4E' },
          { label: 'Kg Procesados', value: `${kgTotal?.toFixed(1)} kg`, color: '#C9A24D' },
          { label: 'Jamones', value: jamones, color: '#8097D6' },
          { label: 'Paletillas', value: paletillas, color: '#8097D6' },
          { label: 'Emplatados', value: emplatados, color: '#8097D6' },
          { label: 'Rentabilidad', value: `${rentabilidad}%`, color: parseFloat(rentabilidad) >= 30 ? '#3A7F6B' : '#C9A24D' },
          { label: 'Último Trabajo', value: lastWork ? lastWork?.toLocaleDateString('es-ES') : '—', color: 'rgba(255,255,255,0.7)' },
          { label: 'Cliente Principal', value: topCliente, color: 'rgba(255,255,255,0.7)' },
        ]?.map(item => (
          <div key={item?.label} className="bg-[#111111] rounded-lg p-3 border border-white/5">
            <p className="text-[10px] font-oranienbaum text-white/40 mb-1">{item?.label}</p>
            <p className="text-sm font-bold font-oranienbaum truncate" style={{ color: item?.color }}>{item?.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Liquidación Modal ────────────────────────────────────────────────────────
const LiquidacionModal = ({ loncheados, empleados, selectedEmpleadoId, filters, formatCurrency, onClose }) => {
  const emp = empleados?.find(e => String(e?.id) === String(selectedEmpleadoId));
  const empLoncheados = selectedEmpleadoId && selectedEmpleadoId !== 'all'
    ? loncheados?.filter(l => String(l?.empleadoId || l?.empleado_id) === String(selectedEmpleadoId))
    : loncheados;

  const totalACobrar = empLoncheados?.reduce((s, l) => {
    const calc = calcularSalarioEmpleado(l?.tipo, l?.pesoLoncheado || l?.peso_loncheado);
    return s + calc?.importe;
  }, 0);
  const totalGenerado = empLoncheados?.reduce((s, l) => s + (parseFloat(l?.tarifaAplicada || l?.importe) || 0), 0);

  const byTipo = {};
  empLoncheados?.forEach(l => {
    const t = (l?.tipo || 'NORMAL')?.toUpperCase();
    const calc = calcularSalarioEmpleado(t, l?.pesoLoncheado || l?.peso_loncheado);
    if (!byTipo?.[t]) byTipo[t] = { count: 0, total: 0, tarifa: calc?.tarifa };
    byTipo[t].count++;
    byTipo[t].total += calc?.importe;
  });

  const periodoLabel = filters?.quickFilter !== 'custom' && filters?.quickFilter
    ? QUICK_FILTERS?.find(f => f?.key === filters?.quickFilter)?.label || 'Periodo seleccionado'
    : filters?.dateFrom && filters?.dateTo
      ? `${filters?.dateFrom} — ${filters?.dateTo}`
      : 'Todos los registros';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4">
      <div className="bg-[#1A1A1A] border border-white/12 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/8 flex items-center justify-between"
          style={{ background: 'rgba(214,239,78,0.05)' }}>
          <div>
            <h2 className="text-xl font-bold font-oranienbaum text-[#D6EF4E]">Liquidación Salarial</h2>
            <p className="text-xs font-oranienbaum text-white/40 mt-0.5">{periodoLabel}</p>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/8 transition-all">
            <Icon name="X" size={16} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Employee */}
          <div className="flex items-center gap-3 p-4 bg-[#111111] rounded-xl border border-white/8">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(214,239,78,0.12)' }}>
              <Icon name="User" size={18} style={{ color: '#D6EF4E' }} />
            </div>
            <div>
              <p className="text-xs font-oranienbaum text-white/40">Empleado</p>
              <p className="text-base font-bold font-oranienbaum text-white">
                {emp?.nombre || 'Todos los empleados'}
              </p>
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#111111] rounded-xl p-4 border border-white/8 text-center">
              <p className="text-[10px] font-oranienbaum text-white/40 mb-1">Nº Trabajos</p>
              <p className="text-2xl font-bold font-oranienbaum text-[#D6EF4E]">{empLoncheados?.length}</p>
            </div>
            <div className="bg-[#111111] rounded-xl p-4 border border-[#3A7F6B]/20 text-center">
              <p className="text-[10px] font-oranienbaum text-white/40 mb-1">Total Generado</p>
              <p className="text-xl font-bold font-oranienbaum text-[#3A7F6B]">{formatCurrency(totalGenerado)}</p>
            </div>
            <div className="bg-[#111111] rounded-xl p-4 border border-[#E05252]/20 text-center">
              <p className="text-[10px] font-oranienbaum text-white/40 mb-1">Total a Pagar</p>
              <p className="text-xl font-bold font-oranienbaum text-[#E05252]">{formatCurrency(totalACobrar)}</p>
            </div>
          </div>

          {/* Breakdown by type */}
          <div>
            <h3 className="text-sm font-bold font-oranienbaum text-[#D6EF4E] mb-3">Desglose por Tipo de Trabajo</h3>
            <div className="space-y-2">
              {Object.entries(byTipo)?.map(([tipo, data]) => (
                <div key={tipo} className="flex items-center justify-between p-3 bg-[#111111] rounded-lg border border-white/6">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-oranienbaum text-white/40 w-20">{tipo}</span>
                    <span className="text-xs font-oranienbaum text-white/60">{data?.count} trabajo{data?.count !== 1 ? 's' : ''}</span>
                    <span className="text-[10px] font-oranienbaum text-white/30">({data?.tarifa})</span>
                  </div>
                  <span className="text-sm font-bold font-oranienbaum text-[#C9A24D]">{formatCurrency(data?.total)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Total destacado */}
          <div className="p-5 rounded-xl border-2 text-center"
            style={{ background: 'rgba(224,82,82,0.08)', borderColor: 'rgba(224,82,82,0.30)' }}>
            <p className="text-sm font-oranienbaum text-white/50 mb-1">TOTAL A PAGAR</p>
            <p className="text-4xl font-bold font-oranienbaum text-[#E05252]">{formatCurrency(totalACobrar)}</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-oranienbaum text-white/60 border border-white/10 hover:border-white/20 hover:text-white transition-all">
              Cerrar
            </button>
            <button
              onClick={() => {
                const content = `LIQUIDACIÓN SALARIAL\n${'='?.repeat(40)}\nEmpleado: ${emp?.nombre || 'Todos'}\nPeriodo: ${periodoLabel}\n\nNº Trabajos: ${empLoncheados?.length}\nTotal Generado: ${formatCurrency(totalGenerado)}\nTotal a Pagar: ${formatCurrency(totalACobrar)}\n\nDESGLOSE:\n${Object.entries(byTipo)?.map(([t, d]) => `  ${t}: ${d?.count} trabajos = ${formatCurrency(d?.total)}`)?.join('\n')}`;
                const blob = new Blob([content], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `liquidacion_${emp?.nombre || 'todos'}_${new Date()?.toISOString()?.split('T')?.[0]}.txt`;
                a?.click();
                URL.revokeObjectURL(url);
              }}
              className="flex-1 py-2.5 rounded-xl text-sm font-bold font-oranienbaum transition-all flex items-center justify-center gap-2"
              style={{ background: 'rgba(214,239,78,0.15)', border: '1px solid rgba(214,239,78,0.30)', color: '#D6EF4E' }}>
              <Icon name="Download" size={15} />
              Exportar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const Salarios = () => {
  const [loncheados, setLoncheados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLiquidacion, setShowLiquidacion] = useState(false);
  const [selectedLoncheado, setSelectedLoncheado] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [filters, setFilters] = useState({
    quickFilter: 'thisMonth',
    dateFrom: '',
    dateTo: '',
    clienteId: 'all',
    empleadoId: 'all',
    tipoGestion: 'all',
    search: '',
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadLoncheados();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [c, e] = await Promise.all([clientService?.getAll(), employeeService?.getAll()]);
      setClientes(c || []);
      setEmpleados(e || []);
    } catch {}
  };

  const buildServiceFilters = useCallback(() => {
    // For custom period: pass UTC ISO strings derived from local Madrid dates
    if (filters?.quickFilter === 'custom') {
      const fromUTC = filters?.dateFrom ? localDateToUTCStart(filters?.dateFrom) : null;
      const toUTCExcl = filters?.dateTo ? localDateToUTCEndExclusive(filters?.dateTo) : null;
      return {
        ...filters,
        period: 'custom',
        dateFromUTC: fromUTC,
        dateToUTCExclusive: toUTCExcl,
      };
    }
    // For quick filters: compute UTC range from the period key
    if (filters?.quickFilter && filters?.quickFilter !== '') {
      const range = getQuickPeriodUTCRange(filters?.quickFilter);
      return {
        ...filters,
        period: 'custom',
        dateFromUTC: range?.fromUTC || null,
        dateToUTCExclusive: range?.toUTCExclusive || null,
      };
    }
    return { ...filters, period: 'all' };
  }, [filters]);

  const loadLoncheados = async () => {
    try {
      setLoading(true);
      setError(null);
      const serviceFilters = buildServiceFilters();
      const lData = await salariosService?.getLoncheadosWithSalaries(serviceFilters);
      setLoncheados(lData || []);
      setCurrentPage(1);
    } catch (err) {
      setError(err?.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'reset') {
      setFilters({ quickFilter: 'thisMonth', dateFrom: '', dateTo: '', clienteId: 'all', empleadoId: 'all', tipoGestion: 'all', search: '' });
    } else if (key === 'quickFilter') {
      if (value === 'custom') {
        // Switching to custom: keep existing dates if any, just set mode
        setFilters(prev => ({ ...prev, quickFilter: 'custom' }));
      } else {
        // Switching to a quick filter: clear manual dates
        setFilters(prev => ({ ...prev, quickFilter: value, dateFrom: '', dateTo: '' }));
      }
    } else if (key === 'dateFrom' || key === 'dateTo') {
      // Entering a manual date automatically activates custom mode
      setFilters(prev => ({ ...prev, [key]: value, quickFilter: 'custom' }));
    } else {
      setFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' })?.format(v || 0);

  // Apply client-side filters (search, tipo, cliente)
  const filteredLoncheados = loncheados?.filter(l => {
    if (filters?.tipoGestion !== 'all' && (l?.tipo || '')?.toUpperCase() !== filters?.tipoGestion) return false;
    if (filters?.clienteId !== 'all' && String(l?.clienteId || l?.cliente_id) !== String(filters?.clienteId)) return false;
    if (filters?.empleadoId !== 'all' && String(l?.empleadoId || l?.empleado_id) !== String(filters?.empleadoId)) return false;
    if (filters?.search) {
      const s = filters?.search?.toLowerCase();
      const matchCliente = (l?.clienteNombre || '')?.toLowerCase()?.includes(s);
      const matchEmpleado = (l?.empleadoNombre || '')?.toLowerCase()?.includes(s);
      const matchProducto = (l?.productoNombre || '')?.toLowerCase()?.includes(s);
      if (!matchCliente && !matchEmpleado && !matchProducto) return false;
    }
    return true;
  });

  // Computed KPIs with salary calculation
  const totalACobrar = filteredLoncheados?.reduce((s, l) => {
    const calc = calcularSalarioEmpleado(l?.tipo, l?.pesoLoncheado || l?.peso_loncheado);
    return s + calc?.importe;
  }, 0);
  const totalGenerado = filteredLoncheados?.reduce((s, l) => s + (parseFloat(l?.tarifaAplicada || l?.importe) || 0), 0);
  const kgProcesados = filteredLoncheados?.reduce((s, l) => s + (parseFloat(l?.pesoLoncheado || l?.peso_loncheado) || 0), 0);
  const mediaEuro = filteredLoncheados?.length > 0 ? totalACobrar / filteredLoncheados?.length : 0;

  // Most productive employee
  const empCount = {};
  filteredLoncheados?.forEach(l => {
    const en = l?.empleadoNombre || '—';
    empCount[en] = (empCount?.[en] || 0) + 1;
  });
  const topEmpleado = Object.entries(empCount)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || '—';

  // Pagination
  const totalRecords = filteredLoncheados?.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const paginatedLoncheados = filteredLoncheados?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const selectClass = "bg-[#111111] border border-white/10 text-white/80 text-sm rounded-lg px-3 py-2 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40 transition-colors";

  return (
    <>
      <Helmet><title>Salarios - Las Dos Doncellas</title></Helmet>
      <ERPPage>
        {/* Header */}
        <ERPPageHeader
          title="Salarios"
          subtitle="Control de costes laborales y liquidaciones"
          actions={
            <>
              <button
                onClick={() => setShowLiquidacion(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold font-oranienbaum transition-all"
                style={{ background: 'rgba(214,239,78,0.15)', border: '1px solid rgba(214,239,78,0.30)', color: '#D6EF4E' }}>
                <Icon name="FileText" size={15} />
                Generar Liquidación
              </button>
              <button
                onClick={() => exportSalariosToCSV(filteredLoncheados, filters)}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/60 border border-white/10 hover:border-white/20 hover:text-white transition-all">
                <Icon name="FileSpreadsheet" size={14} />
                Excel
              </button>
              <button
                onClick={() => exportSalariosToPDF(filteredLoncheados, filters, empleados)}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-oranienbaum text-white/60 border border-white/10 hover:border-white/20 hover:text-white transition-all">
                <Icon name="Download" size={14} />
                PDF
              </button>
            </>
          }
        />

        {/* ── TOTAL A PAGAR — KPI Principal destacado ── */}
        <div className="mb-5 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
          style={{ background: 'linear-gradient(135deg, rgba(224,82,82,0.12) 0%, rgba(26,26,26,1) 60%)', border: '1px solid rgba(224,82,82,0.25)', boxShadow: '0 0 30px rgba(224,82,82,0.08)' }}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(224,82,82,0.15)', border: '1px solid rgba(224,82,82,0.25)' }}>
              <Icon name="Wallet" size={26} style={{ color: '#E05252' }} />
            </div>
            <div>
              <p className="text-sm font-oranienbaum text-white/50 mb-0.5">TOTAL A PAGAR</p>
              <p className="text-4xl lg:text-5xl font-bold font-oranienbaum" style={{ color: '#E05252' }}>
                {loading ? '...' : formatCurrency(totalACobrar)}
              </p>
              <p className="text-xs font-oranienbaum text-white/35 mt-1">
                {filteredLoncheados?.length} trabajos · {QUICK_FILTERS?.find(f => f?.key === filters?.quickFilter)?.label || 'Periodo seleccionado'}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="text-right">
              <p className="text-xs font-oranienbaum text-white/40">Total Generado</p>
              <p className="text-xl font-bold font-oranienbaum text-[#3A7F6B]">{loading ? '...' : formatCurrency(totalGenerado)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-oranienbaum text-white/40">Margen</p>
              <p className="text-base font-bold font-oranienbaum text-[#C9A24D]">
                {loading ? '...' : formatCurrency(totalGenerado - totalACobrar)}
              </p>
            </div>
          </div>
        </div>

        {/* ── 6 KPIs superiores ── */}
        <ERPKPIGrid cols={6}>
          <ERPKPICard title="Total a Pagar" value={formatCurrency(totalACobrar)} subtitle="Coste laboral" icon="Wallet" iconColor="#E05252" valueColor="#E05252" loading={loading} trend={0} trendLabel="" />
          <ERPKPICard title="Total Generado" value={formatCurrency(totalGenerado)} subtitle="Facturación" icon="TrendingUp" iconColor="#3A7F6B" valueColor="#3A7F6B" loading={loading} trend={0} trendLabel="" />
          <ERPKPICard title="Nº Loncheados" value={filteredLoncheados?.length} subtitle="Registros" icon="Package" iconColor="#D6EF4E" valueColor="#D6EF4E" loading={loading} trend={0} trendLabel="" />
          <ERPKPICard title="Kg Procesados" value={`${kgProcesados?.toFixed(1)} kg`} subtitle="Peso total" icon="Scale" iconColor="#C9A24D" valueColor="#C9A24D" loading={loading} trend={0} trendLabel="" />
          <ERPKPICard title="Media €/Trabajo" value={formatCurrency(mediaEuro)} subtitle="Promedio" icon="Calculator" iconColor="#8097D6" valueColor="#8097D6" loading={loading} trend={0} trendLabel="" />
          <ERPKPICard title="Más Productivo" value={topEmpleado} subtitle="Empleado" icon="Award" iconColor="#D6EF4E" valueColor="#D6EF4E" loading={loading} trend={0} trendLabel="" />
        </ERPKPIGrid>

        {/* ── Quick Date Filters ── */}
        <div className="mb-3 flex flex-wrap gap-1.5">
          {QUICK_FILTERS?.map(qf => (
            <button
              key={qf?.key}
              onClick={() => handleFilterChange('quickFilter', qf?.key)}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
              style={filters?.quickFilter === qf?.key
                ? { background: 'rgba(214,239,78,0.15)', border: '1px solid rgba(214,239,78,0.35)', color: '#D6EF4E' }
                : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.55)' }}>
              {qf?.label}
            </button>
          ))}
        </div>

        {/* ── Horizontal Filter Row ── */}
        <div className="mb-5 flex flex-wrap items-center gap-2 p-3 bg-[#1A1A1A] rounded-xl border border-white/8">
          {/* Periodo custom dates */}
          {filters?.quickFilter === 'custom' && (
            <>
              <input type="date" value={filters?.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
                className={selectClass} />
              <input type="date" value={filters?.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
                className={selectClass} />
            </>
          )}

          {/* Empleado */}
          <select value={filters?.empleadoId} onChange={(e) => handleFilterChange('empleadoId', e?.target?.value)}
            className={selectClass}>
            <option value="all">Todos los empleados</option>
            {empleados?.map(e => <option key={e?.id} value={e?.id}>{e?.nombre}</option>)}
          </select>

          {/* Cliente */}
          <select value={filters?.clienteId} onChange={(e) => handleFilterChange('clienteId', e?.target?.value)}
            className={selectClass}>
            <option value="all">Todos los clientes</option>
            {clientes?.map(c => <option key={c?.id} value={c?.id}>{c?.nombre}</option>)}
          </select>

          {/* Tipo de Gestión */}
          <select value={filters?.tipoGestion} onChange={(e) => handleFilterChange('tipoGestion', e?.target?.value)}
            className={selectClass}>
            <option value="all">Tipo de Gestión</option>
            <option value="NORMAL">Normal</option>
            <option value="EMPLATADO">Emplatado</option>
            <option value="PALETILLA">Paletilla</option>
          </select>

          {/* Buscar */}
          <div className="relative flex-1 min-w-[160px]">
            <Icon name="Search" size={13} style={{ color: 'rgba(255,255,255,0.35)', position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Buscar..."
              value={filters?.search}
              onChange={(e) => handleFilterChange('search', e?.target?.value)}
              className="w-full bg-[#111111] border border-white/10 text-white/80 text-sm rounded-lg pl-8 pr-3 py-2 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
            />
          </div>

          {/* Limpiar */}
          <button onClick={() => handleFilterChange('reset', null)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-oranienbaum text-white/50 border border-white/8 hover:border-white/15 hover:text-white transition-all">
            <Icon name="X" size={12} />
            Limpiar
          </button>
        </div>

        {error && <div className="mb-4 p-4 bg-[#E05252]/10 border border-[#E05252]/20 rounded-xl">
          <p className="text-sm font-oranienbaum text-[#E05252]">{error}</p>
        </div>}

        {/* ── Employee Summary (when single employee selected) ── */}
        <EmployeeSummaryCard
          loncheados={filteredLoncheados}
          empleados={empleados}
          selectedEmpleadoId={filters?.empleadoId !== 'all' ? filters?.empleadoId : null}
          formatCurrency={formatCurrency}
        />

        {/* ── Data Table ── */}
        <div className="bg-[#1A1A1A] rounded-xl border border-white/8 overflow-hidden">
          <div className="px-5 py-3 border-b border-white/8 flex items-center justify-between"
            style={{ background: 'rgba(214,239,78,0.04)' }}>
            <h2 className="text-base font-bold font-oranienbaum text-[#D6EF4E]">Registros de Salarios</h2>
            <span className="text-xs font-oranienbaum text-white/40">{totalRecords} registros</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr style={{ background: 'rgba(214,239,78,0.06)', borderBottom: '1px solid rgba(214,239,78,0.15)' }}>
                  {['#', 'Fecha', 'Cliente', 'Empleado', 'Producto', 'Tipo', 'Peso (kg)', 'Tarifa Aplicada', 'Motivo', 'Total a Pagar']?.map(h => (
                    <th key={h} className="px-4 py-4 text-left text-[14px] font-bold font-oranienbaum text-[#D6EF4E] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="py-16 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Icon name="Loader2" size={20} className="animate-spin text-white/30" />
                      <span className="text-sm font-oranienbaum text-white/30">Cargando...</span>
                    </div>
                  </td></tr>
                ) : paginatedLoncheados?.length === 0 ? (
                  <tr><td colSpan={10} className="py-16 text-center">
                    <p className="text-sm font-oranienbaum text-white/30">No hay registros para mostrar</p>
                  </td></tr>
                ) : (
                  paginatedLoncheados?.map((item, idx) => {
                    const globalIdx = (currentPage - 1) * pageSize + idx + 1;
                    const calc = calcularSalarioEmpleado(item?.tipo, item?.pesoLoncheado || item?.peso_loncheado);
                    const tipoUpper = (item?.tipo || 'NORMAL')?.toUpperCase();
                    const tipoColor = tipoUpper === 'EMPLATADO' ? '#D6EF4E' : tipoUpper === 'PALETILLA' ? '#8097D6' : '#3A7F6B';
                    const tipoBg = tipoUpper === 'EMPLATADO' ? 'rgba(214,239,78,0.1)' : tipoUpper === 'PALETILLA' ? 'rgba(128,151,214,0.1)' : 'rgba(58,127,107,0.1)';

                    return (
                      <tr key={item?.id}
                        style={{
                          background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                          borderBottom: '1px solid rgba(255,255,255,0.05)'
                        }}
                        className="hover:bg-white/4 transition-colors">
                        <td className="px-4 py-3.5 text-[13px] font-oranienbaum text-white/35">{globalIdx}</td>
                        <td className="px-4 py-3.5 text-[13px] font-oranienbaum text-white/60 whitespace-nowrap">
                          {item?.createdAt ? new Date(item.createdAt)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3.5 text-[14px] font-oranienbaum text-white/85">{item?.clienteNombre || '—'}</td>
                        <td className="px-4 py-3.5 text-[14px] font-oranienbaum text-white/75">{item?.empleadoNombre || '—'}</td>
                        <td className="px-4 py-3.5 text-[13px] font-oranienbaum text-white/55 max-w-[140px] truncate">{item?.productoNombre || '—'}</td>
                        <td className="px-4 py-3.5">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[12px] font-bold font-oranienbaum"
                            style={{ background: tipoBg, border: `1px solid ${tipoColor}30`, color: tipoColor }}>
                            {tipoUpper}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-[14px] font-oranienbaum text-white/70">
                          {item?.pesoLoncheado != null ? parseFloat(item?.pesoLoncheado)?.toFixed(2) : '—'}
                        </td>
                        <td className="px-4 py-3.5 text-[13px] font-oranienbaum text-white/50">{calc?.tarifa}</td>
                        <td className="px-4 py-3.5 text-[12px] font-oranienbaum text-white/40">{calc?.motivo}</td>
                        <td className="px-4 py-3.5">
                          <span className="text-[15px] font-bold font-oranienbaum text-[#E05252]">
                            {formatCurrency(calc?.importe)}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            />
          )}
        </div>
      </ERPPage>
      {/* Liquidación Modal */}
      {showLiquidacion && (
        <LiquidacionModal
          loncheados={filteredLoncheados}
          empleados={empleados}
          selectedEmpleadoId={filters?.empleadoId}
          filters={filters}
          formatCurrency={formatCurrency}
          onClose={() => setShowLiquidacion(false)}
        />
      )}
    </>
  );
};

export default Salarios;